// deviceSession — this browser's identity as a paired APP DEVICE.
// Spec: docs/specs/device-and-worker-sign-in.md §4.1/§4.5. Phase 2b.
//
// Two identities live side by side and must not be confused:
//   * the DEVICE  — a long-lived bearer token in localStorage, minted once at
//     enrolment. It authorises the sign-in ATTEMPT and nothing else.
//   * the WORKER  — a real Supabase session, minted per shift by the server
//     after a badge and a face. It is what RLS sees.
//
// The device token is deliberately NOT a Supabase session: it can read nothing
// and write nothing on its own. Everything it can do goes through the
// device-sign-in edge function, which re-checks blocked/revoked on every call.

import { supabase } from '@/lib/supabase'

const TOKEN_KEY   = 'kf49.device.token'
const DEVICE_KEY  = 'kf49.device.meta'
/**
 * Per-device secret, minted once at /enrol alongside the bearer token.
 *
 * ⚠️ Kept in a SEPARATE localStorage slot from the bearer token, deliberately —
 * the same split `faceClockHmac` uses. The bearer token authorises requests and
 * is cheap to rotate; this secret is the IKM for the offline cache key and for
 * queue signatures, and rotating it invalidates anything already encrypted or
 * signed with it. An attacker who lifts one slot has not necessarily got both.
 *
 * ⚠️ /enrol has returned this since Phase 2 and pairDevice DISCARDED it until
 * Phase 4b — the edge function's own comment said "Phase 4 signs the offline
 * queue with it". Nothing broke, because nothing used it yet; it would have
 * broken the first time a device tried to encrypt a roster.
 */
const SECRET_KEY  = 'kf49.device.secret'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const ANON_KEY     = import.meta.env.VITE_SUPABASE_ANON_KEY as string
const FN_URL       = `${SUPABASE_URL}/functions/v1/device-sign-in`

export interface DeviceMeta {
  device_id:   string
  name:        string
  profile_key: string
}

/** localStorage can throw (private mode, quota). A device that cannot remember
 *  its pairing is un-paired, which is the safe reading. */
function readLocal(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function writeLocal(key: string, value: string): void {
  try { localStorage.setItem(key, value) } catch { /* un-paired is safe */ }
}

/**
 * The bearer token, or null.
 *
 * Exported since Phase 4e because the drain is a second caller of the same
 * endpoint. Everything that presents it must go through the device-sign-in
 * function — the token authorises nothing against Supabase directly.
 */
export function getDeviceToken(): string | null {
  return readLocal(TOKEN_KEY)
}

export function getDeviceMeta(): DeviceMeta | null {
  const raw = readLocal(DEVICE_KEY)
  if (!raw) return null
  try { return JSON.parse(raw) as DeviceMeta } catch { return null }
}

export function isPairedDevice(): boolean {
  return Boolean(getDeviceToken())
}

/**
 * The per-device secret, or null.
 *
 * null means "this device cannot do offline" — not "use a default". There is no
 * safe fallback key: a fixed or derived-from-nothing key would encrypt every
 * tenant's roster identically, which is worse than not caching at all.
 */
export function getDeviceSecret(): string | null {
  return readLocal(SECRET_KEY)
}

/**
 * Forget the pairing.
 *
 * Also drops any worker session: leaving one alive on a device we no longer
 * recognise is exactly the state the wipe command exists to prevent.
 */
export async function unpairDevice(): Promise<void> {
  try {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(DEVICE_KEY)
    // ⚠️ The secret goes too. Leaving it behind would strand a decryptable
    // roster — names, photos and FACE TEMPLATES — on a device we have just
    // stopped recognising, which is the exact state Wipe exists to prevent.
    localStorage.removeItem(SECRET_KEY)
  } catch { /* best effort */ }
  try { await supabase.auth.signOut() } catch { /* best effort */ }
}

export interface PairResult {
  ok: boolean
  error?: string
  meta?: DeviceMeta
}

/**
 * Did the SERVER fail, rather than the operator's input?
 *
 * ⚠️ This exists because the fallback sentence blames the person holding the
 * tablet. A 404 (function not deployed to this project yet) or a 5xx would
 * otherwise read as "That code was not accepted." — sending an operator off to
 * re-type a code that was always correct, and hiding a deploy fault behind what
 * looks like user error. The endpoint also 404s on a project where the sign-in
 * migration has not been applied, which is exactly the state this branch ships in.
 *
 * A 4xx that is NOT 404 is a real refusal (bad code, blocked device) and keeps
 * the server's own sentence.
 */
function serverFault(status: number): boolean {
  return status === 404 || status >= 500
}

const SERVER_FAULT_MSG =
  'Sign-in is not switched on for this site yet. Nothing is wrong with the code — ask your manager to set it up.'

/**
 * Exchange a one-time enrolment code for this device's bearer token.
 *
 * The token and the HMAC secret are returned ONCE and never again — the server
 * keeps only their hashes. Losing them means enrolling the device afresh.
 */
export async function pairDevice(code: string): Promise<PairResult> {
  try {
    const resp = await fetch(`${FN_URL}/enrol`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', apikey: ANON_KEY },
      body: JSON.stringify({ code: code.trim() }),
    })
    const body = await resp.json().catch(() => ({}))
    if (!resp.ok || !body?.ok) {
      if (serverFault(resp.status)) return { ok: false, error: SERVER_FAULT_MSG }
      return { ok: false, error: body?.error ?? 'That code was not accepted.' }
    }
    const meta: DeviceMeta = {
      device_id: body.device_id, name: body.name, profile_key: body.profile_key,
    }
    writeLocal(TOKEN_KEY, body.device_token)
    writeLocal(DEVICE_KEY, JSON.stringify(meta))
    // Shown ONCE by the server. If it is missing the device is still paired and
    // still works online — it simply cannot hold an encrypted offline cache, and
    // getDeviceSecret() returning null is how the cache layer finds that out.
    if (body.hmac_secret) writeLocal(SECRET_KEY, body.hmac_secret)
    return { ok: true, meta }
  } catch {
    return { ok: false, error: 'Could not reach the server. Check the connection and try again.' }
  }
}

export interface SignInResult {
  ok: boolean
  /** Operator-facing sentence. Always safe to show on the lock screen. */
  error?: string
  worker_number?: string
  display_name?: string
  /** True when the device itself was refused — the screen must un-pair, not retry. */
  deviceRejected?: boolean
  /**
   * True when the request never reached a server (fetch threw).
   *
   * ⚠️ This is the ONLY signal that may trigger the offline fallback, and it is
   * deliberately narrow: a REFUSAL is not offline. If a server said "that isn't
   * you", falling back to a cached roster would let a worker the server just
   * rejected in through the back door — the cache is older than the refusal, so
   * it is exactly the wrong tiebreaker. Only "no server answered" qualifies.
   */
  offline?: boolean
}

interface SignInArgs {
  badgeCode: string
  factor: 'FACE' | 'PIN'
  descriptor?: number[]
  embeddingVersion?: string
  pin?: string
}

/**
 * Ask the server to sign this worker in, and install the session it returns.
 *
 * ⚠️ The descriptor is sent for the SERVER to judge. This function must never
 * decide a match itself, and must never be given the enrolment vectors to
 * compare against — that is the whole reason the online path is safe (§4.4).
 */
export async function deviceSignIn(args: SignInArgs): Promise<SignInResult> {
  const token = getDeviceToken()
  if (!token) return { ok: false, error: 'This device is not set up yet.', deviceRejected: true }

  let resp: Response
  try {
    resp = await fetch(FN_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        apikey: ANON_KEY,
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        badge_code: args.badgeCode,
        factor: args.factor,
        descriptor: args.descriptor,
        embedding_version: args.embeddingVersion,
        pin: args.pin,
      }),
    })
  } catch {
    return {
      ok: false,
      error: 'Could not reach the server. Check the connection.',
      offline: true,
    }
  }

  const body = await resp.json().catch(() => ({}))

  if (!resp.ok || !body?.ok) {
    // A missing or broken endpoint is NOT a refused worker. Reporting it as one
    // sends somebody to re-scan their badge at a server that cannot answer, and
    // (worse) deviceRejected below would be false, so the screen would invite
    // the retry. Fail loudly as a site problem instead.
    if (serverFault(resp.status)) return { ok: false, error: SERVER_FAULT_MSG }
    // 401 on the DEVICE (not the worker) means blocked, revoked or unknown —
    // retrying cannot help, and the screen should stop pretending it can.
    const deviceRejected = resp.status === 401 && !body?.worker_number &&
      /device/i.test(String(body?.error ?? ''))
    return { ok: false, error: body?.error ?? 'Could not sign in.', deviceRejected }
  }

  const { error } = await supabase.auth.setSession({
    access_token: body.access_token,
    refresh_token: body.refresh_token,
  })
  if (error) return { ok: false, error: 'Signed in, but the session could not be stored.' }

  return {
    ok: true,
    worker_number: body.worker_number,
    display_name: body.display_name,
  }
}
